import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  LinearProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import api from "../../utils/api";
import { fileToDataUrl } from "./fileToDataUrl";

/**
 * ImageUploadField — FINAL CONTRACT
 *
 * value  → string | ""     (FULL IMAGE URL FOR PREVIEW ONLY)
 * onChange(publicId|null)  (BACKEND STORAGE VALUE)
 *
 * RULES:
 * - Preview NEVER guesses from public_id
 * - Backend ALWAYS receives public_id only
 * - Full URLs are for UI preview ONLY
 */
export default function ImageUploadField({
  label = "Upload Image",
  value,
  onChange,
}) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  // -------------------------------------------
  // Sync preview ONLY from valid URLs
  // -------------------------------------------
  useEffect(() => {
    if (typeof value === "string" && value.startsWith("http")) {
      setPreview(value);
    } else {
      setPreview("");
    }
  }, [value]);

  const pick = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;

    // 1️⃣ Immediate local preview (UX)
    const localPreview = await fileToDataUrl(file);
    setPreview(localPreview);

    setUploading(true);

    try {
      const fd = new FormData();
      fd.append("file", file);

      const { data } = await api.post("/admin/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // 2️⃣ Switch preview to REAL Cloudinary URL
      if (data?.url) {
        setPreview(data.url);
      }

      // 3️⃣ Send ONLY public_id upward (DB storage)
      onChange?.(data?.public_id || null);
    } catch (err) {
      console.error("Image upload failed:", err);

      // Roll back safely
      setPreview("");
      onChange?.(null);
    } finally {
      setUploading(false);
    }
  };

  const clear = (e) => {
    e.stopPropagation();
    setPreview("");
    onChange?.(null);
  };

  return (
    <Box>
      <Typography variant="overline">{label}</Typography>

      <Paper
        variant="outlined"
        sx={{
          mt: 1,
          p: 2,
          borderRadius: 3,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderStyle: "dashed",
        }}
        onClick={pick}
      >
        {preview ? (
          <img
            src={preview}
            alt="preview"
            style={{
              width: 80,
              height: 80,
              objectFit: "contain",
            }}
          />
        ) : (
          <ImageIcon />
        )}

        <Stack sx={{ flex: 1 }}>
          <Typography>
            {preview ? "Image selected" : "Click to upload"}
          </Typography>
          <Typography variant="caption">
            PNG / JPG / WEBP
          </Typography>
        </Stack>

        {preview && (
          <IconButton size="small" onClick={clear}>
            <DeleteIcon />
          </IconButton>
        )}

        <input
          hidden
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={(e) =>
            handleFile(e.target.files?.[0])
          }
        />
      </Paper>

      {uploading && <LinearProgress sx={{ mt: 1 }} />}
    </Box>
  );
}
